import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SbInstrumentSelectComponent } from '../sb-instrument-select/sb-instrument-select.component';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
    distinctUntilChanged,
    filter,
    map,
    pairwise,
    shareReplay,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import { SbFlowSelectComponent } from '../sb-flow-select/sb-flow-select.component';
import {
    CommercialRealEstate,
    Company,
    FixedIncome,
    Instrument,
    InstrumentType
} from '../../../../shared/state/instruments/instrument.model';
import { AssetType } from '../../../transactions/state/transaction.model';
import { SbAssetTypeSelectComponent } from '../sb-asset-type-select/sb-asset-type-select.component';
import { SbPrivateEquityCompanySelectComponent } from '../../../../shared/components/form/sb-private-equity-company-select/sb-private-equity-company-select.component';
import {
    ActivatedRoute,
    ActivatedRouteSnapshot,
    NavigationEnd,
    Router,
    RouterLink,
    RouterOutlet
} from '@angular/router';
import { combineLatest, merge, of, Subject } from 'rxjs';
import { RouterQuery } from '@datorama/akita-ng-router-store';
import { SbStepIndicatorComponent } from '../sb-step-indicator/sb-step-indicator.component';
import { distinctUntilArrayItemChanged, filterNilValue } from '@datorama/akita';
import { DataEntryStore } from '../../state/data-entry.store';
import { DataEntryQuery } from '../../state/data-entry.query';
import { DataEntryService } from '../../state/data-entry.service';
import { TranslateModule } from '@ngx-translate/core';
import { InstrumentsQuery } from '../../../../shared/state/instruments/instruments.query';
import { SbModalComponent } from '../../../../shared/components/sb-modal/sb-modal.component';
import { SbModalHeaderComponent } from '../../../../shared/components/modal/sb-modal-header/sb-modal-header.component';
import { Fund } from '../../../../pages/authenticated/pe-funds/state/funds.model';

export const getDeepestActivatedRoute = ((
    route: ActivatedRoute | ActivatedRouteSnapshot
): ActivatedRoute | ActivatedRouteSnapshot => {
    while (route.firstChild) {
        route = route.firstChild;
    }
    return route;
}) as ((route: ActivatedRoute) => ActivatedRoute) &
    ((route: ActivatedRouteSnapshot) => ActivatedRouteSnapshot);
@Component({
    selector: 'sb-data-entry-flow',
    standalone: true,
    imports: [
        CommonModule,
        SbInstrumentSelectComponent,
        ReactiveFormsModule,
        SbFlowSelectComponent,
        SbAssetTypeSelectComponent,
        SbPrivateEquityCompanySelectComponent,
        RouterOutlet,
        SbStepIndicatorComponent,
        TranslateModule,
        SbModalComponent,
        SbModalHeaderComponent,
        RouterLink
    ],
    templateUrl: './sb-data-entry-flow.component.html',
    styleUrls: ['./sb-data-entry-flow.component.scss'],
    providers: [DataEntryStore, DataEntryService, DataEntryQuery]
})
export class SbDataEntryFlowComponent implements OnInit, OnDestroy {
    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private routerQuery: RouterQuery,
        private dataEntryStore: DataEntryStore,
        private dataEntryQuery: DataEntryQuery,
        private instrumentsQuery: InstrumentsQuery
    ) {}

    paramStrategy$ = this.route.data.pipe(
        map((data) => data.paramStrategy || null),
        shareReplay(1)
    );

    activeStep$ = this.routerQuery.select('state').pipe(
        switchMap(() => getDeepestActivatedRoute(this.route).data),
        startWith(getDeepestActivatedRoute(this.route.snapshot).data),
        map((data) => data.step || null),
        distinctUntilChanged(),
        shareReplay(1)
    );

    selectInstrumentHeader$ = this.routerQuery.select('state').pipe(
        switchMap(() => getDeepestActivatedRoute(this.route).data),
        startWith(getDeepestActivatedRoute(this.route.snapshot).data),
        map((data) => data.selectInstrumentHeader || null),
        distinctUntilChanged(),
        shareReplay(1)
    );

    addNewUrl$ = this.routerQuery.select('state').pipe(
        switchMap(() => getDeepestActivatedRoute(this.route).data),
        startWith(getDeepestActivatedRoute(this.route.snapshot).data),
        map((data) => data.addNewUrl || null),
        distinctUntilChanged(),
        shareReplay(1)
    );

    titles$ = this.routerQuery.select('state').pipe(
        switchMap(() => getDeepestActivatedRoute(this.route).data),
        distinctUntilChanged(),
        pairwise(),
        startWith([getDeepestActivatedRoute(this.route.snapshot).data]),
        withLatestFrom(this.activeStep$),
        map(([titles, activeStep]) =>
            titles
                .filter((data) => data.title !== null)
                .sort((a, b) => a.step - b.step)
                .reduce((acc, curr) => {
                    if (
                        curr.step <= activeStep &&
                        (acc.length === 0 || curr.step === acc[acc.length - 1].step + 1)
                    ) {
                        acc.push(curr);
                    }
                    return acc;
                }, [])
        ),
        map((data) =>
            data.length > 1 && data[0].step === 1 ? data.slice(1, data.length) : data
        ),
        map((data) => data.map((data) => data.title)),
        shareReplay(1)
    );

    unsubscribe$ = new Subject();
    assetTypeCtrl = new FormControl<AssetType>({
        value: this.route.snapshot?.firstChild?.routeConfig.path
            ?.toUpperCase()
            .replace(/-/g, '_') as AssetType, //this.route.snapshot.params?.assetType?.toUpperCase().replace(/-/g, '_'),
        disabled: false
    });
    instrumentTypes$ = this.assetTypeCtrl.valueChanges.pipe(
        startWith(this.assetTypeCtrl.value),
        map((assetType) => {
            let instrumentTypes: Array<InstrumentType>;
            switch (assetType) {
                case 'PRIVATE_EQUITY':
                    instrumentTypes = [
                        'PRIVATE_EQUITY',
                        'PE_OTHER_SHARE',
                        'PE_PREF_SHARE',
                        'PE_CONVERTIBLE',
                        'PE_LOAN',
                        'PE_OPTION'
                    ];
                    break;
                case 'PRIVATE_EQUITY_FUND':
                    instrumentTypes = ['PRIVATE_FUND'];
                    break;
                case 'FIXED_INCOME':
                    instrumentTypes = ['BOND'];
                    break;
                default:
                    instrumentTypes = [assetType as InstrumentType];
                    break;
            }
            return instrumentTypes;
        }),
        shareReplay(1)
    );

    instrumentControl = new FormControl<Instrument>({ value: null, disabled: false });
    // TODO: this needs to be refactored (simplified and more stable)
    // instrument id sometimes gets replaced which causes errors and inconsistent behavior
    instrumentId$ = merge(
        this.instrumentControl.valueChanges.pipe(
            map((instrument) => instrument?.instrumentId || null)
        ),
        // subscribe to router events and update companyInstrumentId if companyInstrumentId in url path has changed with navigation
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map(
                (res) =>
                    +getDeepestActivatedRoute(this.route.snapshot).params
                        ?.companyInstrumentId ||
                    +getDeepestActivatedRoute(this.route.snapshot).params?.instrumentId
            ),
            filter((id) => !!id),
            distinctUntilChanged()
        ),
        of(
            +getDeepestActivatedRoute(this.route.snapshot).params?.instrumentId ||
                +this.routerQuery.getParams('instrumentId')
        ).pipe(filterNilValue()),
        of(
            +getDeepestActivatedRoute(this.route.snapshot).params?.companyInstrumentId ||
                +this.routerQuery.getParams('companyInstrumentId')
        ).pipe(filterNilValue()),
        of(this.instrumentControl.value).pipe(filterNilValue())
    ).pipe(
        map((instrumentId) => (instrumentId ? +instrumentId : null)),
        distinctUntilChanged(),
        shareReplay(1)
    );

    assetType$ = this.assetTypeCtrl.valueChanges.pipe(
        startWith(this.assetTypeCtrl.value),
        distinctUntilChanged(),
        tap((assetType) => {
            const urlPath = assetType.toLowerCase().replace(/_/g, '-');
            if (this.route.snapshot.firstChild.routeConfig.path !== urlPath) {
                // reset instrumentId
                this.instrumentControl.reset();

                // switch asset type
                this.router.navigate([`./${urlPath}`], {
                    relativeTo: this.route
                });
            }
        }),
        shareReplay(1)
    );

    existingOptions$ = this.assetType$.pipe(
        switchMap((assetType) => {
            switch (assetType) {
                case 'PRIVATE_EQUITY':
                    return this.instrumentsQuery.selectInstrumentsByFilter$<Company>({
                        type: { eq: 'COMPANY' },
                        deleted: { eq: false }
                    });
                case 'PRIVATE_EQUITY_FUND':
                    return this.instrumentsQuery.selectInstrumentsByFilter$<Fund>({
                        type: { eq: 'PRIVATE_FUND' },
                        deleted: { eq: false }
                    });
                case 'FIXED_INCOME':
                    return this.instrumentsQuery.selectInstrumentsByFilter$<FixedIncome>({
                        type: { eq: 'BOND' },
                        deleted: { eq: false }
                    });
                case 'COMMERCIAL_REAL_ESTATE':
                    return this.instrumentsQuery.selectInstrumentsByFilter$<CommercialRealEstate>(
                        {
                            type: { eq: 'COMMERCIAL_REAL_ESTATE' },
                            deleted: { eq: false }
                        }
                    );
                default:
                    return of([]);
            }
        }),
        map((options) => options.length > 0),
        distinctUntilChanged(),
        shareReplay(1)
    );

    flowPath$ = this.routerQuery.select('state').pipe(
        map((state) => {
            const match = state.urlAfterRedirects.match(
                /(?:\(?modal:data-entry\/)(?<assetType>[\w\-]*)\/?(?<instrumentId>\d*)?\/?(?<flowPath>[\w\-\/]*)?\)?/
            );
            return [
                match?.groups?.assetType,
                match?.groups?.instrumentId,
                ...(match?.groups?.flowPath?.split('/') || [])
            ].filter((path) => !!path) as Array<string | number>;
        }),
        distinctUntilArrayItemChanged(),
        shareReplay(1)
    );

    formState$ = this.flowPath$.pipe(
        filter((flowPath) => flowPath.length > 2),
        map((path) => path.join('/')),
        tap((path) => this.dataEntryStore.setActive(path)),
        switchMap((path) => this.dataEntryQuery.selectFormState(path)),
        startWith(null),
        shareReplay(1),
        takeUntil(this.unsubscribe$)
    );

    steps$ = combineLatest([this.activeStep$, this.flowPath$, this.formState$]).pipe(
        map(([activeStep, flowPath, formState]) => {
            return {
                1: {
                    title: 'Choose action',
                    path: activeStep ? flowPath?.slice(0, 2) : null,
                    state:
                        activeStep > 1
                            ? 'completed'
                            : activeStep === 1
                            ? 'active'
                            : 'inactive'
                },
                2: {
                    title: 'Enter data',
                    path: activeStep >= 2 ? flowPath?.slice(0, 3) : null,
                    state:
                        activeStep > 2
                            ? 'completed'
                            : activeStep === 2
                            ? formState
                            : 'inactive'
                },
                3: {
                    title: 'Confirmation',
                    path: activeStep >= 3 ? flowPath?.slice(0, 4) : null,
                    state: activeStep === 3 ? 'completed' : 'inactive'
                }
            } as { [key: number]: any };
        }),
        filterNilValue(),
        shareReplay(1)
    );
    ngOnInit(): void {
        this.instrumentId$
            .pipe(
                filterNilValue(),
                withLatestFrom(this.assetType$),
                takeUntil(this.unsubscribe$)
            )
            .subscribe(([instrumentId, assetType]) => {
                if (!this.router.url.includes('data-entry')) {
                    // temporary fix to avoid faulty navigation as the new loan forms do not support id parameter navigation
                    return;
                }
                const deepestActivatedRoute = getDeepestActivatedRoute(
                    this.route.snapshot
                );
                const id =
                    +deepestActivatedRoute.params.instrumentId ||
                    +deepestActivatedRoute.params.companyInstrumentId;

                if (instrumentId && instrumentId !== id) {
                    let path = [`./${assetType.toLowerCase().replace(/_/g, '-')}`];
                    if (instrumentId !== null) {
                        path.push(instrumentId.toString());
                    }

                    this.router.navigate(path, {
                        relativeTo: this.route
                    });
                }

                if (
                    typeof instrumentId === 'number' &&
                    this.instrumentControl.value?.instrumentId !== instrumentId
                ) {
                    this.instrumentsQuery
                        .selectInstrumentById$(instrumentId)
                        .pipe(filterNilValue(), take(1), takeUntil(this.unsubscribe$))
                        .subscribe((instrument) => {
                            this.instrumentControl.setValue(instrument);
                        });
                }
            });
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
